import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  alpha,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
  Stack,
  Paper,
  Grid
} from '@mui/material';
import { publicInstance } from '../axiosInstance';
import { CalibrationType } from '../InventoryPanel/types';
import { getReagentColor } from '../InventoryPanel/utils';
import ScienceIcon from '@mui/icons-material/Science';

interface CalibrationInformationProps {
  deviceNumber: string | undefined;
}

const CalibrationInformation: React.FC<CalibrationInformationProps> = ({ deviceNumber }) => {
  const [calibrations, setCalibrations] = useState<CalibrationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.name === 'dark';

  useEffect(() => {
    const fetchCalibrationData = async () => {
      if (!deviceNumber) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await publicInstance.get(`/view_dacapi_calib_info_merged?box=eq.${deviceNumber}`);
        
        if (response.data && response.data.length > 0 && response.data[0].calibrations) {
          setCalibrations(response.data[0].calibrations);
        } else {
          setCalibrations([]);
        }
      } catch (error) {
        console.error('Error fetching calibration data:', error);
        setError('Failed to load calibration data');
      } finally {
        setLoading(false);
      }
    };

    fetchCalibrationData();
  }, [deviceNumber]);

  // Format date to compact format: MM/DD/YYYY
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <Card 
        elevation={isDarkMode ? 2 : 1}
        sx={{ 
          mt: 2, 
          width: '100%',
          borderRadius: '4px',
          backgroundColor: theme.palette.background.paper
        }}
      >
        <CardHeader
          avatar={<ScienceIcon color="primary" />}
          title={<Typography variant="h6">Calibration Information</Typography>}
        />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        elevation={isDarkMode ? 2 : 1}
        sx={{ 
          mt: 2, 
          width: '100%',
          borderRadius: '4px',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.borders.primary}`
        }}
      >
        <CardHeader
          avatar={<ScienceIcon color="error" />}
          title={<Typography variant="h6">Calibration Information</Typography>}
        />
        <CardContent sx={{ py: 1 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (calibrations.length === 0) {
    return (
      <Card 
        elevation={isDarkMode ? 2 : 1}
        sx={{ 
          mt: 2, 
          width: '100%',
          borderRadius: '4px',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.borders.primary}`
        }}
      >
        <CardHeader
          avatar={<ScienceIcon color="primary" />}
          title={<Typography variant="h6">Calibration Information</Typography>}
        />
        <CardContent sx={{ py: 1 }}>
          <Typography variant="body2">No calibration data available</Typography>
        </CardContent>
      </Card>
    );
  }

  // Group calibrations by reagent type for better organization
  const groupedCalibrations = calibrations.reduce((acc, calibration) => {
    const reagent = calibration.reagent;
    if (!acc[reagent]) {
      acc[reagent] = [];
    }
    acc[reagent].push(calibration);
    return acc;
  }, {} as Record<string, CalibrationType[]>);

  // Sort reagents so PM always comes first, then alphabetically
  const sortedReagents = Object.entries(groupedCalibrations).sort(([reagentA], [reagentB]) => {
    if (reagentA === 'PM') return -1;
    if (reagentB === 'PM') return 1;
    return reagentA.localeCompare(reagentB);
  });

  return (
    <Card 
      elevation={isDarkMode ? 2 : 1}
      sx={{ 
        mt: 2, 
        width: '100%',
        borderRadius: '4px',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.borders.primary}`
      }}
    >
      <CardHeader
        avatar={<ScienceIcon color="primary" />}
        title={<Typography variant="h6">Calibration Information</Typography>}
      />
      <CardContent sx={{ py: 1, px: 2, maxHeight: 230, overflowY: 'auto' }}>
        <Grid container spacing={1}>
          {sortedReagents.map(([reagent, calibs]) => (
            <Grid item xs={12} sm={6} key={reagent}>
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  height: '100%',
                  borderRadius: '4px',
                  backgroundColor: alpha(getReagentColor(reagent), isDarkMode ? 0.08 : 0.04),
                  border: `1px solid ${alpha(getReagentColor(reagent), isDarkMode ? 0.15 : 0.1)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box 
                    sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: getReagentColor(reagent),
                      mr: 1
                    }} 
                  />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: isDarkMode ? getReagentColor(reagent) : theme.palette.text.primary
                    }}
                  >
                    {reagent}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${calibs.length}`}
                    sx={{ 
                      ml: 'auto', 
                      height: '18px',
                      fontSize: '0.65rem',
                      backgroundColor: alpha(getReagentColor(reagent), 0.1),
                      color: isDarkMode ? getReagentColor(reagent) : 'inherit'
                    }} 
                  />
                </Box>
                
                <Divider sx={{ my: 0.5 }} />
                
                <Stack spacing={0.25}>
                  {calibs.map((calibration) => (
                    <Box 
                      key={calibration.id} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        px: 0.5,
                        py: 0.25,
                      }}
                    >
                      <Typography variant="body2">
                        #{calibration.calibration_number}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {formatDate(calibration.start_calibration)} — {formatDate(calibration.end_calibration)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CalibrationInformation; 